var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { InlineSelect, Text } from '@ui';
import { RESULT_FAIL, RESULT_NONE, RESULT_PASS } from '../constants';
import { useAttachmentFieldCommit } from '../hooks/useAttachmentFieldCommit';
/**
 * Inline-editable `Result` cell — the `answer` enum, plus a "None" option the enum itself has no
 * member for (the column is simply NULL).
 *
 * `RESULT_NONE` is a UI-only sentinel and is converted back to `null` before it reaches the wire:
 * assigning `""` or `"none"` to a Rails enum raises ArgumentError, which is why the controller
 * also normalises blank to NULL. Both ends guard it.
 *
 * `InlineSelect` is genuinely controlled, so a failed commit reverts for free — the displayed
 * value never moved. Only the inline error needs surfacing.
 */
export function AttachmentResultCell({ item, inspectionId, translateFn }) {
    var _a;
    const { commit } = useAttachmentFieldCommit(inspectionId, item.id);
    const [answer, setAnswer] = useState((_a = item.answer) !== null && _a !== void 0 ? _a : RESULT_NONE);
    const [error, setError] = useState(null);
    // Refetch-clobber guard — see InlineNameCell for the full reasoning. A listing refetch replaces
    // `item` without remounting this cell, and our own commit must not be undone by it.
    const isSavingRef = useRef(false);
    useEffect(() => {
        var _a;
        if (isSavingRef.current)
            return;
        setAnswer((_a = item.answer) !== null && _a !== void 0 ? _a : RESULT_NONE);
    }, [item.answer]);
    const handleChange = (nextValue) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (nextValue === answer)
            return;
        isSavingRef.current = true;
        setError(null);
        const result = yield commit({ answer: nextValue === RESULT_NONE ? null : nextValue });
        isSavingRef.current = false;
        if (result.success) {
            setAnswer((_a = result.row.answer) !== null && _a !== void 0 ? _a : RESULT_NONE);
            setError(null);
        }
        else {
            setError(result.error);
        }
    });
    return (React.createElement("span", { className: "attachments-surface__result-cell", "data-action-cell": true },
        React.createElement(InlineSelect, { options: [
                { value: RESULT_NONE, label: translateFn('result.none') },
                { value: RESULT_PASS, label: translateFn('result.pass') },
                { value: RESULT_FAIL, label: translateFn('result.fail') }
            ], value: answer, onChange: (value) => {
                void handleChange(value);
            } }),
        error && (React.createElement(Text, { variant: "body", role: "alert", className: "attachments-surface__field-error" }, error))));
}
